/**
 * Functions to fetch book data directly from the BuxBox Story Builder database.
 * This is the "Direct Read" architecture — Builder is the single source of truth for content.
 */
import { builderSupabase } from '@/integrations/builder/client';
import type {
  BuilderBook,
  BuilderPage,
  BuilderCharacter,
  BuilderMusicTrack,
  BuilderCoverReference,
} from '@/integrations/builder/types';
import type { Book, CompiledBook, BookPage } from '@/types/book';

/**
 * Fetch the full catalog of live books from the Builder.
 */
export async function getBuilderCatalog(): Promise<BuilderBook[]> {
  const [booksRes, coversRes] = await Promise.all([
    builderSupabase
      .from('books')
      .select('*')
      .or('is_live.eq.true,is_coming_soon.eq.true')
      .order('created_at', { ascending: false }),
    builderSupabase.storage.from('page-images').list('covers', { limit: 200 }),
  ]);

  if (booksRes.error) {
    console.error('[Builder] Error fetching catalog:', booksRes.error);
    return [];
  }

  // Build map: bookId -> latest cover filename (by timestamp in name)
  const coverMap = new Map<string, string>();
  if (coversRes.data) {
    for (const file of coversRes.data) {
      // Pattern: {bookId}-cover-{timestamp}.png
      const match = file.name.match(/^(.+?)-cover-(\d+)\.png$/);
      if (match) {
        const [, bookId, ts] = match;
        const existing = coverMap.get(bookId);
        if (!existing || file.name > existing) {
          coverMap.set(bookId, file.name);
        }
      }
    }
  }

  return (booksRes.data ?? []).map((b: any) => {
    const coverFileName = coverMap.get(b.id);
    const cover_url = coverFileName
      ? builderSupabase.storage.from('page-images').getPublicUrl(`covers/${coverFileName}`).data.publicUrl
      : null;
    return { ...b, hero: b.is_hero ?? false, cover_url };
  }) as BuilderBook[];
}

/**
 * Fetch a single book with all its related data (pages, characters, music, cover refs).
 */
export async function getBuilderBookFull(bookId: string) {
  const [bookRes, pagesRes, charsRes, musicRes, coverRes] = await Promise.all([
    builderSupabase.from('books').select('*').eq('id', bookId).single(),
    builderSupabase.from('pages').select('*').eq('book_id', bookId).order('page_number'),
    builderSupabase.from('characters').select('*').eq('book_id', bookId),
    builderSupabase.from('book_music_tracks').select('*').eq('book_id', bookId).order('page_start'),
    builderSupabase.from('cover_references').select('*').eq('book_id', bookId),
  ]);

  if (bookRes.error) {
    console.error('[Builder] Error fetching book:', bookRes.error);
    return null;
  }

  return {
    book: bookRes.data as BuilderBook,
    pages: (pagesRes.data ?? []) as BuilderPage[],
    characters: (charsRes.data ?? []) as BuilderCharacter[],
    musicTracks: (musicRes.data ?? []) as BuilderMusicTrack[],
    coverReferences: (coverRes.data ?? []) as BuilderCoverReference[],
  };
}

/**
 * Convert a Builder book + pages into the Reader's Book & CompiledBook format.
 * This is the bridge between the two systems.
 */
export function mapBuilderToReaderBook(
  builderBook: BuilderBook,
  pages: BuilderPage[],
  characters: BuilderCharacter[],
  musicTracks: BuilderMusicTrack[],
  coverReferences: BuilderCoverReference[],
  localBookId?: number, // Optional local DB ID for purchase mapping
): Book {
  // Find cover image from page 1 image or cover references
  const coverRef = coverReferences.find(cr => cr.stored_url);
  const firstPageImage = pages.find(p => p.page_number === 1)?.image_url;
  const coverImageUrl = coverRef?.stored_url ?? firstPageImage ?? null;

  // Build compiled_book pages
  const compiledPages: BookPage[] = pages.map((p) => ({
    content: p.text,
    pageNumber: p.page_number,
    imageUrl: p.image_url,
    audioUrl: p.audio_url,
    type: p.page_number === 0 ? 'cover' : 'story' as const,
    formatting: undefined,
  }));

  // Build imagesByPage lookup
  const imagesByPage: Record<string | number, any> = {};
  pages.forEach((p) => {
    if (p.image_url) {
      const img = { pageNumber: p.page_number, url: p.image_url };
      imagesByPage[p.page_number] = img;
      imagesByPage[String(p.page_number)] = img;
    }
  });

  // Build audio page URLs
  const pageAudioUrls: Record<string, string> = {};
  pages.forEach((p) => {
    if (p.audio_url) {
      pageAudioUrls[`page_${p.page_number}`] = p.audio_url;
    }
  });

  // Build word timings by page
  const wordTimingsByPage: Record<string, any> = {};
  pages.forEach((p) => {
    if (p.word_timings) {
      wordTimingsByPage[`page_${p.page_number}`] = p.word_timings;
    }
  });

  // Map characters
  const characterDescriptions = characters.map((c) => ({
    name: c.parody_name || c.name,
    description: c.identity_block || c.clothing_style,
    traits: c.personality_traits,
  }));

  // Map music tracks to soundtrack format
  const soundtrack = musicTracks.map((t) => ({
    mood: t.mood,
    description: t.mood_description,
    url: t.music_url,
    pageStart: t.page_start,
    pageEnd: t.page_end,
  }));

  const compiledBook: CompiledBook = {
    pages: compiledPages,
    metadata: {
      totalPages: compiledPages.length,
      hasImages: pages.some((p) => !!p.image_url),
      template: 'builder',
      chapterPages: [],
      paragraphsPerPage: 1,
    },
    imagesByPage,
    title: builderBook.title,
    shortDescription: builderBook.short_description ?? undefined,
    characterDescriptions,
    soundtrack,
    audioEnabled: pages.some((p) => !!p.audio_url),
    audio: {
      pageAudioUrls,
    },
  };

  // Attach word timings directly to compiled pages for inline highlighting
  compiledPages.forEach((cp) => {
    const key = `page_${cp.pageNumber}`;
    if (wordTimingsByPage[key]) {
      (cp as any).wordTimings = wordTimingsByPage[key];
    }
  });

  return {
    id: localBookId ?? 0, // 0 means no local ID mapped yet
    title: builderBook.title,
    author: builderBook.movie_inspiration,
    description: builderBook.long_description ?? builderBook.summary,
    short_description: builderBook.short_description,
    cover_image_url: coverImageUrl,
    file_path: null,
    created_at: builderBook.created_at,
    updated_at: builderBook.updated_at,
    price: 0,
    bundle_id: null,
    bundle_price: null,
    tags: builderBook.tags,
    compiled_book: compiledBook,
    character_descriptions: characterDescriptions,
    // Builder-specific metadata we carry along
    is_preview: true, // Builder books default to accessible for now
    availability_type: builderBook.is_coming_soon ? 'coming_soon' : 'free',
  };
}

/**
 * High-level: Fetch a Builder book by UUID and return it in Reader format.
 */
export async function fetchBuilderBookForReader(builderBookId: string, localBookId?: number): Promise<Book | null> {
  const result = await getBuilderBookFull(builderBookId);
  if (!result) return null;

  return mapBuilderToReaderBook(
    result.book,
    result.pages,
    result.characters,
    result.musicTracks,
    result.coverReferences,
    localBookId,
  );
}
